// A readied action (qa-runs#366; docs/MRCS/feats.md §10b): on their turn a combatant states a trigger in words and a kind
// of action, and it is held on the encounter (`held`). The DM or the player fires it when the trigger happens: the
// combatant moves to just before whoever acts now, takes that place in the order and acts; when it ends its turn, the one
// it broke in on goes on as it was (`resume`). The turn it fires is that one kind of action, not a whole turn. Unused, it is lost as the combatant's next turn starts. The rule is the
// system's (`features.combat.ready`): a system with none has no Ready button.
//
// Pure. What is held is the DM's whole, and a player is sent only their own combatants' (`heldSeen`): the trigger's words
// reach nobody else, and another combatant's is absent, not masked. The body carries them, so it reaches players never:
// every encounter body goes out as the projected encounter (`projectEvent`).
import { actNow } from './delay.js';
import { currentTurn, turnKey } from './encounter.js';
export const READY_LIMITS = { trigger: 200 };
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isKey = (v) => typeof v === 'string' && /^[a-z][a-z0-9_]{0,62}$/.test(v);
const isTrigger = (v) => typeof v === 'string' && v.trim().length > 0 && v.length <= READY_LIMITS.trigger && !/[\u0000-\u001f]/.test(v);
/** `features.combat.ready`, strictly (the data and the view have the same keys); null when malformed. */
export function parseReadyRule(v) {
    if (!isObj(v) || Object.keys(v).some((k) => !['label', 'spends', 'kinds'].includes(k)))
        return null;
    if (typeof v.label !== 'string' || !v.label.trim() || v.label.length > 40 || !isKey(v.spends))
        return null;
    if (!Array.isArray(v.kinds) || !v.kinds.length || v.kinds.length > 10 || !v.kinds.every(isKey))
        return null;
    return { label: v.label.trim(), spends: v.spends, kinds: [...v.kinds] };
}
export const NO_READY_RULE = "This table's game system has no readied action.";
export const CANNOT_READY = 'Only the combatant whose turn it is can ready an action, one at a time.';
export const BAD_READY_KIND = "That kind of action can't be readied.";
export const NOT_READIED = "That combatant isn't holding a readied action.";
export const OWN_TURN = 'It is their own turn: the action goes now.';
/** A body, strictly. `encounter.readyLost` is a kind only the server commits (SYSTEM_KINDS). */
export function parseReadyBody(b) {
    if (b.kind === 'encounter.readyLost')
        return Array.isArray(b.ids) && b.ids.length >= 1 && b.ids.length <= 200 && b.ids.every((x) => typeof x === 'string' && UUID_RE.test(x)) ? { kind: 'encounter.readyLost', ids: [...b.ids] } : null;
    if (typeof b.id !== 'string' || !UUID_RE.test(b.id))
        return null;
    if (b.kind === 'encounter.fire')
        return { kind: 'encounter.fire', id: b.id };
    const t = isObj(b.turn) ? b.turn : null;
    if (b.kind !== 'encounter.ready' || !t || typeof t.round !== 'number' || typeof t.active !== 'string' || !isTrigger(b.trigger) || !isKey(b.action))
        return null;
    return { kind: 'encounter.ready', id: b.id, turn: { round: t.round, active: t.active, ...(typeof t.pass === 'number' ? { pass: t.pass } : {}) }, trigger: b.trigger.trim(), action: b.action };
}
/** Why a Ready is refused now, or null. */
export function readyRefusal(enc, body, rule) {
    if (!rule)
        return NO_READY_RULE;
    const now = currentTurn(enc);
    if (!now || turnKey(now) !== turnKey(body.turn) || now.active !== body.id || (enc.held ?? []).some((h) => h.id === body.id))
        return CANNOT_READY;
    return rule.kinds.includes(body.action) ? null : BAD_READY_KIND;
}
/** Why a Fire is refused now, or null. */
export function fireRefusal(enc, id, rule) {
    if (!rule)
        return NO_READY_RULE;
    if (!enc.running || !(enc.held ?? []).some((h) => h.id === id))
        return NOT_READIED;
    return enc.combatants[enc.activeIndex]?.id === id ? OWN_TURN : null;
}
/** Fire: as an Act now that takes its place; the turn broken in on is kept for when the order comes back to it. */
const TAKES_PLACE = { until: 'own_turn', takesPlace: true, label: '' };
export function fireReady(enc, id) {
    const moved = actNow(enc, id, TAKES_PLACE);
    return { ...moved, held: (enc.held ?? []).filter((h) => h.id !== id), ...(enc.turn ? { resume: [...(enc.resume ?? []), enc.turn] } : {}) };
}
/** The kinds a Fire's turn has used already: every counted kind of the turn but the one that was held (a free action uses none). */
export function firedSpends(rule, held) {
    return (rule?.kinds ?? []).filter((k) => k.perTurn !== null && k.perTurn > 0 && k.key !== held).map((k) => k.key);
}
/** After End turn: the combatant the order reached goes on with the turn a Fire broke in on. A record of another round is dropped. */
export function resumeTurn(enc) {
    const list = enc.resume;
    if (!list?.length)
        return enc;
    const active = enc.combatants[enc.activeIndex];
    const mine = list.find((t) => t.combatant === active?.id && t.round === enc.round);
    const rest = list.filter((t) => t !== mine && t.round === enc.round);
    const { resume: _r, ...base } = enc;
    return { ...base, ...(mine ? { turn: mine } : {}), ...(rest.length ? { resume: rest } : {}) };
}
/** What has run out in the encounter as it now stands: a holder gone or the fight over, or a later round's turn of its own. */
export function expiredReady(enc) {
    const active = enc.running ? enc.combatants[enc.activeIndex] : undefined;
    return (enc.held ?? []).filter((h) => !enc.running || !enc.combatants.some((c) => c.id === h.id) || (active?.id === h.id && enc.round > h.round)).map((h) => h.id);
}
/** The reducer's half. A list emptied is no list, as a fresh encounter has none. */
export function reduceReady(enc, body) {
    const out = (() => {
        switch (body.kind) {
            case 'encounter.ready': return { ...enc, held: [...(enc.held ?? []), { id: body.id, round: enc.round, trigger: body.trigger, action: body.action }] };
            case 'encounter.fire': return (enc.held ?? []).some((h) => h.id === body.id) ? fireReady(enc, body.id) : enc;
            case 'encounter.readyLost': return { ...enc, held: (enc.held ?? []).filter((h) => !body.ids.includes(h.id)) };
        }
    })();
    if (out.held && !out.held.length) {
        const { held: _h, ...rest } = out;
        return rest;
    }
    return out;
}
/** What a player is sent: their own combatants' alone. */
export function heldSeen(held, mine) {
    const out = (held ?? []).filter((h) => mine(h.id)).map((h) => ({ ...h }));
    return out.length ? out : undefined;
}
/** A stored list, strictly: each of a combatant still in the fight, once. For `normaliseEncounter`. */
export function normaliseHeld(v, combatants) {
    if (!Array.isArray(v))
        return undefined;
    const out = [];
    for (const h of v) {
        if (!isObj(h) || typeof h.id !== 'string' || !combatants.some((c) => c.id === h.id) || out.some((x) => x.id === h.id))
            continue;
        if (typeof h.round !== 'number' || !Number.isInteger(h.round) || h.round < 1 || !isTrigger(h.trigger) || !isKey(h.action))
            continue;
        out.push({ id: h.id, round: h.round, trigger: h.trigger, action: h.action });
    }
    return out.length ? out : undefined;
}
//# sourceMappingURL=ready.js.map